import { useState, useEffect, useCallback, useRef } from 'react';
import { useGroupActions } from '@/hooks/useGroupActions';
import { useGroupStore } from '@/stores/useGroupStore';
import { useFileMedia } from '@/hooks/useFileMedia';
import { GroupListItem, UpdateGroupParams } from '@/api/types';
import { logPrint, preventDuplicateClick } from '@/utils/my-utils';
import { hasPermission } from '@/utils/permission-user-utils';

interface GroupEditPanelProps {
  group: GroupListItem;
  onBack: () => void;
}

// 颜色名称到十六进制的映射
// 定义群组头像颜色枚举
enum DefaultGroupAvatarColor {
  Purple = 'purple',
  Pink = 'pink',
  Red = 'red',
  Orange = 'orange',
  Yellow = 'yellow',
  Green = 'green',
  Teal = 'teal',
  Blue = 'blue'
}

// 颜色名称到十六进制的映射
const COLOR_NAME_MAP: Record<string, string> = {
  [DefaultGroupAvatarColor.Purple]: '#9333ea',
  [DefaultGroupAvatarColor.Pink]: '#ec4899',
  [DefaultGroupAvatarColor.Red]: '#ef4444',
  [DefaultGroupAvatarColor.Orange]: '#f97316',
  [DefaultGroupAvatarColor.Yellow]: '#f59e0b',
  [DefaultGroupAvatarColor.Green]: '#10b981',
  [DefaultGroupAvatarColor.Teal]: '#14b8a6',
  [DefaultGroupAvatarColor.Blue]: '#3b82f6'
};

// 十六进制到颜色名称的映射（用于发送到后台）
const COLOR_HEX_MAP: Record<string, string> = {
  '#9333ea': DefaultGroupAvatarColor.Purple,
  '#ec4899': DefaultGroupAvatarColor.Pink,
  '#ef4444': DefaultGroupAvatarColor.Red,
  '#f97316': DefaultGroupAvatarColor.Orange,
  '#f59e0b': DefaultGroupAvatarColor.Yellow,
  '#10b981': DefaultGroupAvatarColor.Green,
  '#14b8a6': DefaultGroupAvatarColor.Teal,
  '#3b82f6': DefaultGroupAvatarColor.Blue
};

// 预定义的默认颜色选项（十六进制）
const DEFAULT_COLORS = Object.values(COLOR_NAME_MAP);

/**
 * 将颜色名称转换为十六进制
 * @param colorName 颜色名称
 * @returns 十六进制颜色值
 */
const getHexFromColorName = (colorName: string): string => {
  return COLOR_NAME_MAP[colorName.toLowerCase()] || DEFAULT_COLORS[0];
};

/**
 * 将十六进制颜色转换为颜色名称
 * @param hexColor 十六进制颜色值
 * @returns 颜色名称
 */
const getColorNameFromHex = (hexColor: string): string => {
  return COLOR_HEX_MAP[hexColor.toLowerCase()] || 'purple';
};

/**
 * 群组编辑面板
 * 允许修改群组名称、默认颜色等信息
 */
export default function GroupEditPanel({
  group,
  onBack
}: GroupEditPanelProps) {
  const [groupName, setGroupName] = useState('');
  const [defaultColor, setDefaultColor] = useState('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isModified, setIsModified] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { updateGroup, isLoading: updating, error: updateError } = useGroupActions();
  const { updateGroupInList } = useGroupStore();
  const { 
    uploadAvatar, 
    isLoading: uploading, 
    error: uploadError,
    clearError: clearUploadError
  } = useFileMedia();

  // 检查权限
  const canEditGroup = hasPermission('CREATE_GROUP'); // 使用创建群组权限来控制编辑

  // 初始化表单数据
  useEffect(() => {
    if (group) {
      setGroupName(group.name || '');
      // 处理颜色：如果是颜色名称则转换为十六进制，如果是十六进制则直接使用
      let initialColor = DEFAULT_COLORS[0];
      if (group.default_avatar_color) {
        // 检查是否是十六进制颜色
        if (group.default_avatar_color.startsWith('#')) {
          initialColor = group.default_avatar_color;
        } else {
          // 假设是颜色名称，转换为十六进制
          initialColor = getHexFromColorName(group.default_avatar_color);
        }
      }
      setDefaultColor(initialColor);
      setIsModified(false);
    }
  }, [group]);

  // 检查是否有修改
  useEffect(() => {
    const nameChanged = groupName !== (group.name || '');
    
    // 获取原始颜色的十六进制值进行比较
    let originalColor = DEFAULT_COLORS[0];
    if (group.default_avatar_color) {
      originalColor = group.default_avatar_color.startsWith('#') 
        ? group.default_avatar_color 
        : getHexFromColorName(group.default_avatar_color);
    }
    const colorChanged = defaultColor !== originalColor;
    const avatarChanged = avatarFile !== null;
    
    setIsModified(nameChanged || colorChanged || avatarChanged);
  }, [groupName, defaultColor, avatarFile, group.name, group.default_avatar_color]);

  /**
   * 处理头像文件选择
   */
  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      alert('请选择图片文件');
      return;
    }

    // 验证文件大小 (限制为 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('图片文件大小不能超过 5MB');
      return;
    }

    setAvatarFile(file);
    
    // 生成预览
    const reader = new FileReader();
    reader.onload = (e) => {
      setAvatarPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // 清除上传错误
    clearUploadError();
  };

  /**
   * 清除头像选择
   */
  const handleClearAvatar = () => {
    setAvatarFile(null);
    setAvatarPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * 触发文件选择
   */
  const handleSelectAvatarClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * 处理保存
   */
  const handleSave = useCallback(
    preventDuplicateClick(async () => {
      if (!group || !isModified) return;

      try {
        let avatarMediaUrl: string | undefined;
        
        // 如果有选择新头像，先上传头像
        if (avatarFile) {
          const uploadResult = await uploadAvatar(avatarFile, (progress: any) => {
            logPrint(`群组头像上传进度: ${progress}%`);
          });
          
          if (!uploadResult) {
            alert('头像上传失败，请重试');
            return;
          }
          
          avatarMediaUrl = uploadResult.accessUrl;
          logPrint('群组头像上传成功，媒体URL:', avatarMediaUrl);
        }

        const params: UpdateGroupParams = {
          name: groupName.trim() || undefined,
          default_avatar_color: getColorNameFromHex(defaultColor) || undefined,
          avatar: avatarMediaUrl || undefined
        };

        const success = await updateGroup(group.id, params);
        if (success) {
          // 更新本地store中的群组信息
          const updateData: Partial<GroupListItem> = {
            name: groupName.trim(),
            default_avatar_color: defaultColor
          };
          
          // 如果上传了头像，也更新头像字段
          if (avatarMediaUrl) {
            updateData.avatar = avatarMediaUrl;
          }
          
          updateGroupInList(group.id, updateData);
          
          // 保存成功后返回详情页
          onBack();
        }
      } catch (error) {
        console.error('保存群组信息失败:', error);
      }
    }),
    [group, isModified, groupName, defaultColor, avatarFile, updateGroup, updateGroupInList, uploadAvatar, onBack]
  );

  /**
   * 处理取消
   */
  const handleCancel = () => {
    if (isModified) {
      const confirmed = confirm('您有未保存的修改，确定要取消吗？');
      if (!confirmed) return;
    }
    onBack();
  };

  /**
   * 处理键盘事件
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isModified && !updating) {
      handleSave();
    }
  };

  /**
   * 渲染颜色选择器
   */
  const renderColorPicker = () => (
    <div className="space-y-3">
      <div className="flex flex-row justify-between gap-2">
        {DEFAULT_COLORS.map((color) => (
          <button
            key={color}
            type="button"
            onClick={() => setDefaultColor(color)}
            className={`w-8 h-8 rounded-full border-2 transition-all ${
              defaultColor === color 
                ? 'border-gray-400 scale-110' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            style={{ backgroundColor: color }}
            title={getColorNameFromHex(color)}
          >
            {defaultColor === color && (
              <svg className="w-4 h-4 text-white mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        ))}
      </div>
      {/* 显示当前选中的颜色名称 */}
      <p className="text-xs text-gray-600">
        当前选择: <span className="font-medium capitalize">{getColorNameFromHex(defaultColor)}</span>
      </p>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-white">
      {/* 头部 */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <button
            onClick={handleCancel}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h3 className="text-lg font-semibold text-gray-900">编辑群组</h3>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleCancel}
            className="px-3 py-1 text-sm font-medium text-gray-700 hover:text-gray-800 border border-gray-300 hover:border-gray-400 rounded transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            disabled={updating || uploading || !isModified || !groupName.trim() || !canEditGroup}
            className="px-3 py-1 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded transition-colors"
          >
            {updating ? '保存中...' : uploading ? '上传中...' : '保存'}
          </button>
        </div>
      </div>

      {/* 内容区域 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* 权限检查 */}
        {!canEditGroup && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">您暂无编辑群组权限</p>
          </div>
        )}
        {/* 群组基本信息预览 */}
        <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
          <div 
            className="h-12 w-12 rounded-full flex items-center justify-center text-white font-medium"
            style={{ backgroundColor: defaultColor }}
          >
            {groupName.charAt(0)?.toUpperCase() || group.name.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <p className="font-medium text-gray-900">{groupName || group.name}</p>
            <p className="text-sm text-gray-500">群组ID: {group.id}</p>
          </div>
        </div>

        {/* 群组名称输入 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            群组名称 *
          </label>
          <input
            type="text"
            placeholder="请输入群组名称"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={!canEditGroup}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            maxLength={50}
          />
          <p className="text-xs text-gray-500 mt-1">{groupName.length}/50</p>
        </div>

        {/* 默认颜色选择 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            默认头像颜色
          </label>
          {renderColorPicker()}
          <p className="text-xs text-gray-500 mt-2">
            选择群组默认头像的背景颜色
          </p>
        </div>

        {/* 头像上传 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            群组头像
          </label>
          
          {/* 头像预览区域 */}
          <div className="flex items-start space-x-4">
            {/* 当前头像显示 */}
            <div className="flex flex-col items-center space-y-2">
              <div className="relative">
                {avatarPreview ? (
                  // 显示新选择的头像预览
                  <img 
                    src={avatarPreview} 
                    alt="头像预览" 
                    className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : group.avatar ? (
                  // 显示现有头像
                  <img 
                    src={group.avatar} 
                    alt="群组头像" 
                    className="h-20 w-20 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  // 显示默认颜色头像
                  <div 
                    className="h-20 w-20 rounded-full flex items-center justify-center text-white text-2xl font-medium border-2 border-gray-200"
                    style={{ backgroundColor: defaultColor }}
                  >
                    {groupName.charAt(0)?.toUpperCase() || group.name.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
                
                {/* 上传中指示器 */}
                {uploading && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <svg className="animate-spin h-6 w-6 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                )}
              </div>
              
              {avatarPreview && (
                <span className="text-xs text-green-600 font-medium">新头像预览</span>
              )}
            </div>
            
            {/* 上传控制区域 */}
            <div className="flex-1 space-y-3">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                {!avatarFile ? (
                  <>
                    <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <button
                      type="button"
                      onClick={handleSelectAvatarClick}
                      disabled={!canEditGroup || uploading}
                      className="text-sm font-medium text-purple-600 hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                    >
                      选择头像图片
                    </button>
                    <p className="text-xs text-gray-500 mt-1">支持 JPG、PNG 格式，最大 5MB</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-center space-x-2 text-sm text-gray-700">
                      <svg className="h-4 w-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>已选择: {avatarFile.name}</span>
                    </div>
                    <div className="flex justify-center space-x-2 mt-2">
                      <button
                        type="button"
                        onClick={handleSelectAvatarClick}
                        disabled={!canEditGroup || uploading}
                        className="text-xs text-purple-600 hover:text-purple-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        重新选择
                      </button>
                      <span className="text-xs text-gray-400">|</span>
                      <button
                        type="button"
                        onClick={handleClearAvatar}
                        disabled={!canEditGroup || uploading}
                        className="text-xs text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        清除选择
                      </button>
                    </div>
                  </>
                )}
              </div>
              
              {/* 上传错误提示 */}
              {uploadError && (
                <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
                  {uploadError}
                </div>
              )}
            </div>
          </div>
          
          {/* 隐藏的文件输入 */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarSelect}
            className="hidden"
          />
          
          <p className="text-xs text-gray-500 mt-2">
            上传群组头像后，所有成员都能看到新头像
          </p>
        </div>

        {/* 说明文本 */}
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-800">
            <strong>提示：</strong>修改群组信息后，所有成员都会看到更新
          </p>
        </div>

        {/* 错误信息 */}
        {(updateError || uploadError) && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg space-y-1">
            {updateError && <p className="text-sm text-red-800">{updateError}</p>}
            {uploadError && <p className="text-sm text-red-800">头像上传失败: {uploadError}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
